// Le consentement OAuth (lot 7.3) : un client MCP (Claude Desktop, Claude Code…) demande l'accès à Alfred. La personne
// choisit, portée par portée, ce qu'il peut lire ; ce qu'il voudra écrire passera de toute façon par « À valider ».
// Accepter crée un accès, visible et révocable dans Réglages → Appareils.

import { useEffect, useState } from 'react';

import type { Actions } from '../App';
import { Fenetre } from '../components';
import { NonConnecte, api } from '../lib/api';

const PORTEES: Record<string, { titre: string; detail: string }> = {
  agenda: { titre: 'Votre agenda', detail: 'Lire vos rendez-vous et le point du jour.' },
  rappels: { titre: 'Vos rappels', detail: 'Les lire ; en créer ou en cocher, avec votre validation.' },
  cerveau: { titre: 'Votre cerveau', detail: 'Chercher et lire vos notes, lire ce qu’Alfred sait de vous ; proposer un souvenir, avec votre validation.' },
};

export function Oauth({ params, actions }: { params: URLSearchParams; actions: Actions }) {
  const [client, setClient] = useState<{ nom: string; portees: string[] } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [cochees, setCochees] = useState<string[]>(['agenda', 'rappels', 'cerveau']);
  const [envoi, setEnvoi] = useState(false);
  const clientId = params.get('client_id') ?? '';
  const redirectUri = params.get('redirect_uri') ?? '';

  useEffect(() => {
    api.oauthClient(clientId, redirectUri).then(setClient).catch((e: unknown) => {
      if (e instanceof NonConnecte) window.location.hash = '#/connexion';
      else setErreur((e as Error).message);
    });
  }, [clientId, redirectUri]);

  const repondre = async (accepte: boolean) => {
    setEnvoi(true);
    try {
      const r = await api.oauthAutoriser({
        client_id: clientId, redirect_uri: redirectUri, code_challenge: params.get('code_challenge') ?? '',
        ...(params.get('state') ? { state: params.get('state')! } : {}), portees: cochees, accepte,
      });
      // Retour au client (une adresse locale pour Claude Desktop ou Claude Code) : il termine la connexion lui-même.
      window.location.href = r.redirection;
    } catch (e) {
      actions.toast({ texte: (e as Error).message, erreur: true });
      setEnvoi(false);
    }
  };

  if (erreur) return <main className="ecran centre"><p className="bandeau-erreur">{erreur}</p></main>;
  if (!client) return <main className="ecran centre"><p className="message muted">Chargement…</p></main>;
  return (
    <main className="ecran">
      <Fenetre>
        <p className="entretien-question">« {client.nom} » demande l’accès à Alfred</p>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Un assistant extérieur (Claude, par le MCP). Ce qu’il voudra écrire attendra toujours votre validation sur votre téléphone.
        </p>
        {client.portees.map((p) => (
          <label key={p} className="entretien-case ligne-retenue">
            <input type="checkbox" checked={cochees.includes(p)} onChange={(e) => setCochees(e.target.checked ? [...cochees, p] : cochees.filter((x) => x !== p))} />
            <span><b>{PORTEES[p]?.titre ?? p}</b><br /><small className="muted">{PORTEES[p]?.detail}</small></span>
          </label>
        ))}
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>Vous pourrez retirer cet accès à tout moment dans Réglages → Appareils.</p>
      </Fenetre>
      <div className="boutons">
        <button className="btn" onClick={() => void repondre(false)} disabled={envoi}>Refuser</button>
        <button className="btn principal" onClick={() => void repondre(true)} disabled={envoi || !cochees.length}>Autoriser</button>
      </div>
    </main>
  );
}
